import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@clerk/clerk-react";
import { getServerURL } from "../../../utils/funcs/get-serverURL";
import { formatDisplayDate } from "../../../utils/funcs/formatDisplayDate";
import { capitaliseFirstLetter } from "../../../utils/funcs/capitalise-first-letter";
import { BottomSpacer } from "../../spacer";
import { MdDownload, MdOpenInNew } from "react-icons/md";

interface RemedialBookingSummary {
  status: "quote_sent" | "checkout_created" | "paid" | "cancelled";
  customerEmail: string;
  totalAmount: number;
  scheduledDate?: string;
  paidAt?: string;
  createdAt: string;
}

interface MyConstructorRecord {
  _id: string;
  customerName: string;
  customerPhone: string;
  customerAddress?: {
    line1?: string;
    line2?: string;
    city?: string;
    postcode?: string;
  };
  inspectionDate: string;
  result: "Pass" | "Fail";
  totalQuoteAmount?: number;
  eicrFileUrl?: string;
  claimedEmail?: string;
  approvalStatus: string;
  emailSent: boolean;
  createdAt: string;
  remedialBooking: RemedialBookingSummary | null;
}

const REMEDIAL_STATUS_LABELS: Record<string, { label: string; bg: string; color: string }> = {
  quote_sent:       { label: "Quote Sent",    bg: "#fef3c7", color: "#92400e" },
  checkout_created: { label: "In Checkout",   bg: "#e0f2fe", color: "#0369a1" },
  paid:             { label: "Paid ✓",        bg: "#dcfce7", color: "#16a34a" },
  cancelled:        { label: "Cancelled",     bg: "#fee2e2", color: "#dc2626" },
};

export default function MyConstructorEICR() {
  const { getToken } = useAuth();
  const [records, setRecords] = useState<MyConstructorRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedPdfUrl, setSelectedPdfUrl] = useState<string | null>(null);

  const serverURL = getServerURL();

  const getAuthHeader = useCallback(async () => {
    const token = await getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, [getToken]);

  const fetchRecords = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const headers = await getAuthHeader();
      const res = await axios.get(`${serverURL}/eicr-records/myconstructor`, { headers });
      if (res.data.success) {
        setRecords(res.data.records || []);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to load MyConstructor records");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader, serverURL]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  const openPdf = useCallback(
    async (record: MyConstructorRecord) => {
      try {
        const headers = await getAuthHeader();
        const fileName = `${record.customerName || "certificate"}-eicr.pdf`;
        const res = await axios.get(`${serverURL}/serve-eicr-pdf`, {
          params: { fileUrl: record.eicrFileUrl, fileName },
          headers,
          responseType: "blob",
        });
        const blobUrl = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
        setSelectedPdfUrl(blobUrl);
      } catch {
        window.open(record.eicrFileUrl, "_blank");
      }
    },
    [getAuthHeader, serverURL]
  );

  const downloadPdf = useCallback(
    async (record: MyConstructorRecord) => {
      try {
        const headers = await getAuthHeader();
        const fileName = `${record.customerName || "certificate"}-eicr.pdf`;
        const res = await axios.get(`${serverURL}/serve-eicr-pdf`, {
          params: { fileUrl: record.eicrFileUrl, fileName },
          headers,
          responseType: "blob",
        });

        const blobUrl = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(blobUrl);
      } catch {
        window.open(record.eicrFileUrl, "_blank");
      }
    },
    [getAuthHeader, serverURL]
  );

  const filteredRecords = records.filter((r) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      r.customerName.toLowerCase().includes(q) ||
      r.customerPhone.includes(q) ||
      (r.customerAddress?.postcode || "").toLowerCase().includes(q) ||
      (r.claimedEmail || "").toLowerCase().includes(q) ||
      (r.remedialBooking?.customerEmail || "").toLowerCase().includes(q)
    );
  });

  const formatAddress = (addr?: MyConstructorRecord["customerAddress"]) => {
    if (!addr) return "—";
    return [addr.line1, addr.line2, addr.city, addr.postcode].filter(Boolean).join(", ") || "—";
  };

  // Derive the best known email: remedial booking email if they booked, claimed email if they claimed via SMS, else empty
  const getBestEmail = (record: MyConstructorRecord) => {
    if (record.remedialBooking?.customerEmail) return record.remedialBooking.customerEmail;
    if (record.claimedEmail) return record.claimedEmail;
    return null;
  };

  return (
    <>
      <h2 style={{ marginTop: 0, marginBottom: "6px", color: "#333" }}>MyConstructor EICR Records</h2>
      <p style={{ margin: "0 0 20px 0", color: "#666", fontSize: "0.85em", textAlign: "left" }}>
        EICR records uploaded without a customer email — these originated from MyConstructor bookings.
        The email column shows the address we received if the customer later claimed their certificate or booked remedial works.
      </p>

      <input
        type="text"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        placeholder="Search by name, phone, postcode or email…"
        style={{
          width: "100%",
          padding: "10px 14px",
          border: "1px solid #ddd",
          borderRadius: "6px",
          fontSize: "0.9em",
          marginBottom: "20px",
          boxSizing: "border-box",
        }}
      />

      {error && (
        <p style={{ color: "#dc2626", marginBottom: "16px" }}>{error}</p>
      )}

      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: "40px 0" }}>
          <div className="loadingBtn"><div /><div /><div /><div /></div>
        </div>
      ) : filteredRecords.length === 0 ? (
        <p style={{ color: "#999", fontStyle: "italic" }}>
          {records.length === 0 ? "No MyConstructor records found." : "No records match your search."}
        </p>
      ) : (
        <div style={{ overflowX: "auto", borderRadius: "6px", border: "1px solid #e0e0e0" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", backgroundColor: "#fff", minWidth: "700px", textAlign: "left" }}>
            <thead>
              <tr style={{ backgroundColor: "#f8f9fa", borderBottom: "2px solid #e0e0e0" }}>
                <th style={thStyle}>Customer</th>
                <th style={thStyle}>Phone</th>
                <th style={thStyle}>Address</th>
                <th style={thStyle}>Inspection</th>
                <th style={thStyle}>Result</th>
                <th style={thStyle}>Email</th>
                <th style={thStyle}>Remedial Status</th>
                <th style={thStyle}>Certificate</th>
              </tr>
            </thead>
            <tbody>
              {filteredRecords.map((record, index) => {
                const email = getBestEmail(record);
                const remedial = record.remedialBooking;
                const statusInfo = remedial ? REMEDIAL_STATUS_LABELS[remedial.status] : null;

                return (
                  <tr
                    key={record._id}
                    style={{
                      borderBottom: "1px solid #e0e0e0",
                      backgroundColor: index % 2 === 0 ? "#fff" : "#f8f9fa",
                      textAlign: "left",
                    }}
                    >
                    <td style={tdStyle}>
                      <div style={{ fontWeight: 500 }}>{capitaliseFirstLetter(record.customerName)}</div>
                      <div style={{ fontSize: "0.75em", color: "#888" }}>
                        {formatDisplayDate(record.createdAt)}
                      </div>
                    </td>
                    <td style={tdStyle}>{record.customerPhone}</td>
                    <td style={tdStyle}>
                      <div>{formatAddress(record.customerAddress)}</div>
                    </td>
                    <td style={tdStyle}>{formatDisplayDate(record.inspectionDate)}</td>
                    <td style={tdStyle}>
                      <span style={{
                        backgroundColor: record.result === "Pass" ? "#dcfce7" : "#fee2e2",
                        color: record.result === "Pass" ? "#16a34a" : "#dc2626",
                        padding: "3px 8px",
                        borderRadius: "4px",
                        fontWeight: 600,
                        fontSize: "0.8em",
                      }}>
                        {record.result}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      {email ? (
                        <span style={{ color: "#333" }}>{email}</span>
                      ) : (
                        <span style={{ color: "#bbb", fontStyle: "italic" }}>Not yet received</span>
                      )}
                    </td>
                    <td style={tdStyle}>
                      {record.result === "Pass" ? (
                        <span style={{ color: "#999", fontSize: "0.8em" }}>N/A</span>
                      ) : remedial && statusInfo ? (
                        <div>
                          <span style={{
                            backgroundColor: statusInfo.bg,
                            color: statusInfo.color,
                            padding: "3px 8px",
                            borderRadius: "4px",
                            fontWeight: 600,
                            fontSize: "0.8em",
                          }}>
                            {statusInfo.label}
                          </span>
                          {remedial.status === "paid" && remedial.scheduledDate && (
                            <div style={{ fontSize: "0.75em", color: "#666", marginTop: "4px" }}>
                              {formatDisplayDate(remedial.scheduledDate)}
                            </div>
                          )}
                          {remedial.totalAmount > 0 && (
                            <div style={{ fontSize: "0.75em", color: "#666" }}>
                              £{remedial.totalAmount.toFixed(2)}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span style={{ color: "#bbb", fontSize: "0.8em", fontStyle: "italic" }}>No booking</span>
                      )}
                    </td>
                    <td style={tdStyle}>
                      {record.eicrFileUrl ? (
                        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                          <button
                            onClick={() => openPdf(record)}
                            style={{
                              background: "none",
                              border: "none",
                              color: "#00adb5",
                              cursor: "pointer",
                              fontSize: "1.2em",
                              padding: "4px",
                            }}
                            title="View Certificate"
                          >
                            <MdOpenInNew />
                          </button>
                          <button
                            onClick={() => downloadPdf(record)}
                            style={{
                              background: "none",
                              border: "none",
                              color: "#00adb5",
                              cursor: "pointer",
                              fontSize: "1.2em",
                              padding: "4px",
                            }}
                            title="Download Certificate"
                          >
                            <MdDownload />
                          </button>
                        </div>
                      ) : (
                        <span style={{ color: "#bbb" }}>—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* PDF Modal */}
      {selectedPdfUrl && (
        <div
          style={{
            position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: "rgba(0,0,0,0.7)",
            display: "flex", alignItems: "center", justifyContent: "center",
            zIndex: 1000,
          }}
          onClick={() => setSelectedPdfUrl(null)}
        >
          <div
            style={{
              backgroundColor: "#fff", borderRadius: "8px", overflow: "hidden",
              width: "90vw", maxWidth: "900px", height: "85vh",
              display: "flex", flexDirection: "column",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              padding: "12px 16px", borderBottom: "1px solid #e0e0e0",
            }}>
              <span style={{ fontWeight: 600, color: "#333" }}>EICR Certificate</span>
              <button
                onClick={() => setSelectedPdfUrl(null)}
                style={{
                  background: "none", border: "none", fontSize: "1.4em",
                  cursor: "pointer", color: "#666", lineHeight: 1,
                }}
              >
                ×
              </button>
            </div>
            <iframe src={selectedPdfUrl} style={{ flex: 1, border: "none" }} title="EICR Certificate" />
          </div>
        </div>
      )}

      <BottomSpacer />
    </>
  );
}

const thStyle: React.CSSProperties = {
  padding: "12px 8px",
  textAlign: "left",
  fontWeight: 600,
  fontSize: "0.8em",
  color: "#333",
};

const tdStyle: React.CSSProperties = {
  padding: "12px 8px",
  fontSize: "0.8em",
  color: "#333",
  textAlign: "left",
  verticalAlign: "top",
};
